// REWORK: Full mercenary creation flow with alignment, templates, and RPG-style UI
import React, { useState, useRef, ChangeEvent, FormEvent, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import Toast from '../../Shared/Toast';
import HoldButton from '../../Shared/HoldButton';
import styles from './CreateMercenary.module.css';

interface PrimaryPoints {
  health: number;
  strength: number;
  endurance: number;
  initiative: number;
  dodge: number;
  axe: number;
  sword: number;
  mace: number;
  staff: number;
  shield: number;
  dagger: number;
  chain: number;
}

interface SecondaryPoints {
  learning: number;
  luck: number;
  discipline: number;
  leadership: number;
  provocation: number;
}

interface RaceModifiers {
  raceName: string;
  health: number;
  strength: number;
  endurance: number;
  learningCapacity: number;
  discipline: number;
  initiative: number;
  avoidance: number;
  luck: number;
  weaponAxes: number;
  weaponSwords: number;
  weaponMaces: number;
  weaponStaves: number;
  weaponDaggers: number;
  weaponChain: number;
  weaponShields: number;
  weight: number;
  minimumCritChance: number;
  maximumCritChance: number;
  offHandDamage: number;
}

interface SkillTemplate {
  id: number;
  name: string;
  race: string;
  description: string | null;
  health: number;
  strength: number;
  endurance: number;
  initiative: number;
  dodge: number;
  axe: number;
  sword: number;
  mace: number;
  staff: number;
  shield: number;
  dagger: number;
  chain: number;
  learning: number;
  luck: number;
  discipline: number;
  leadership: number;
  provocation: number;
}

type Alignment = 'Good' | 'Neutral' | 'Evil';

const initialPrimaryPoints: PrimaryPoints = {
  health: 0, strength: 0, endurance: 0, initiative: 0, dodge: 0,
  axe: 0, sword: 0, mace: 0, staff: 0, shield: 0, dagger: 0, chain: 0,
};

const initialSecondaryPoints: SecondaryPoints = {
  learning: 0, luck: 0, discipline: 0, leadership: 0, provocation: 0,
};

const RACE_CONFIG: Record<string, { lore: string; abilities: { label: string; desc: string }[] }> = {
  Human: {
    lore: 'Adaptable and ambitious, masters of coordination.',
    abilities: [
      { label: 'Leadership Roar', desc: '30% chance per round to boost own initiative & leadership and cascade bonuses to nearby allies.' },
    ],
  },
  Dwarf: {
    lore: 'Stout and resilient, forged in iron and stone.',
    abilities: [
      { label: 'Natural Resilience', desc: 'Reduces every incoming hit by flat absorption, scaling with level.' },
      { label: 'Enhanced Weapon', desc: 'Round 1: 50% chance to enhance the blade, dramatically boosting crit chance for the first strike.' },
    ],
  },
  Elf: {
    lore: 'Graceful and swift, turning agility into raw power.',
    abilities: [
      { label: 'Avoiding Shout', desc: 'After evading 3 attacks, agility surges into strength, for the rest of the battle.' },
    ],
  },
  Orc: {
    lore: 'Savage and ferocious, stronger when bloodied.',
    abilities: [
      { label: 'Berserk', desc: 'First time HP drops below 90%, strength surges for the rest of the battle.' },
      { label: 'Vicious Strikes', desc: 'Every critical hit deals extra crit damage on top of all other bonuses.' },
    ],
  },
  Troll: {
    lore: 'Massive and terrifying, capable of shattering blows.',
    abilities: [
      { label: 'Brute Force', desc: 'Damage cap raised by 20% on every attack, enabling higher maximum hits.' },
      { label: 'Intimidation', desc: 'Round 1: 65% chance per attack to suppress the target\'s dodge entirely.' },
    ],
  },
  Goblin: {
    lore: 'Cunning and nimble, every ounce of muscle counts.',
    abilities: [
      { label: 'Physique Mastery', desc: 'Total physical stats(STR+END+INI+DOD) adds a bonus to all four core stats.' },
      { label: 'Dirty Tricks', desc: '20% chance when attacked to throw grit in the attacker\'s face, reducing their next attacks hit chance.' },
    ],
  },
};

const ALIGNMENT_CONFIG: Record<Alignment, { icon: string; description: string; repText: string }> = {
  Good:    { icon: '☀', description: 'Bound by honour and light.',         repText: 'High chance to gain reputation.' },
  Neutral: { icon: '⚖', description: 'The outcome shapes the path.',       repText: 'Actions determine reputation.' },
  Evil:    { icon: '☽', description: 'Power through infamy and fear.',      repText: 'High chance to lose reputation.' },
};

const PRIMARY_ATTRIBUTE_SKILLS = ['health', 'strength', 'endurance', 'initiative', 'dodge'] as const;
const PRIMARY_WEAPON_SKILLS    = ['axe', 'sword', 'mace', 'staff', 'shield', 'dagger', 'chain'] as const;
const SECONDARY_SKILLS         = ['learning', 'luck', 'discipline', 'leadership', 'provocation'] as const;

const SKILL_DISPLAY: Record<string, string> = {
  health: 'Health', strength: 'Strength', endurance: 'Endurance', initiative: 'Initiative', dodge: 'Dodge',
  axe: 'Axe', sword: 'Sword', mace: 'Mace', staff: 'Staff', shield: 'Shield', dagger: 'Dagger', chain: 'Chain',
  learning: 'Learning', luck: 'Luck', discipline: 'Discipline', leadership: 'Leadership', provocation: 'Provocation',
};

const SKILL_DESCRIPTIONS: Record<string, string> = {
  health: 'Increases your Hit Points (HP) and overall durability in combat. Your HP is capped at 3x your total Strength - investing more Health than that is wasted until you also raise Strength.',
  strength: 'Increases physical damage dealt and carrying capacity, reducing penalties from heavy equipment. Also raises your HP cap - maximum HP cannot exceed 3x your total Strength.',
  endurance: 'Improves resilience and stamina, allowing you to fight for more rounds before exhaustion.',
  initiative: 'Determines turn order in combat. Crucial for heavy weapon users to strike before the enemy.',
  dodge: 'Increases your chance to completely evade incoming attacks.',
  axe: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  sword: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  mace: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  staff: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  shield: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  dagger: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  chain: 'Increases accuracy, parry chance, and shield block chance with this weapon type.',
  learning: 'Increases experience gained from battles and other activities.',
  luck: 'Increases chances of winning in gambling minigames and finding better loot.',
  discipline: 'Increases the maximum number of daily rounds your mercenary can perform.',
  leadership: 'Provides a stat bonus to all party members in team battles (only applies from the leader with the highest skill).',
  provocation: 'Increases the likelihood of being targeted by enemies, protecting weaker allies.',
};

const weaponMapping: Record<string, string> = {
  axe: 'weaponAxes', sword: 'weaponSwords', mace: 'weaponMaces',
  staff: 'weaponStaves', shield: 'weaponShields', dagger: 'weaponDaggers', chain: 'weaponChain',
};
const attributeMapping: Record<string, string> = {
  health: 'health', strength: 'strength', endurance: 'endurance', initiative: 'initiative', dodge: 'avoidance',
};
const secondarySkillMapping: Record<string, string> = {
  learning: 'learningCapacity', luck: 'luck', discipline: 'discipline', leadership: '', provocation: '',
};

const CreateMercenary: React.FC = () => {
  const { switchMercenary, fetchUserMercenaries, setMercenary } = useMercenary();
  const { socket } = useSocket();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [name, setName]                   = useState<string>('');
  const [creationBlocked, setCreationBlocked]         = useState<boolean>(false);
  const [creationBlockedMessage, setCreationBlockedMessage] = useState<string>('');
  const [race, setRace]                   = useState<string>('Human');
  const [sex, setSex]                     = useState<string>('Male');
  const [alignment, setAlignment]         = useState<Alignment>('Neutral');
  const [primaryPoints, setPrimaryPoints] = useState<PrimaryPoints>(initialPrimaryPoints);
  const [secondaryPoints, setSecondaryPoints] = useState<SecondaryPoints>(initialSecondaryPoints);
  const [primaryPointsLeft, setPrimaryPointsLeft]     = useState<number>(120);
  const [secondaryPointsLeft, setSecondaryPointsLeft] = useState<number>(5);
  const [avatar, setAvatar]               = useState<number | null>(null);
  const [isNameAvailable, setIsNameAvailable] = useState<boolean | null>(null);
  const [nameError, setNameError]         = useState<string | null>(null);
  const [raceBonuses, setRaceBonuses]     = useState<RaceModifiers[]>([]);
  const [skillTemplates, setSkillTemplates] = useState<SkillTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);
  const [toastMessage, setToastMessage]   = useState<string | null>(null);
  const [toastType, setToastType]         = useState<'success' | 'error'>('success');
  const [toastDuration, setToastDuration] = useState<number>(4000);
  const [activeSection, setActiveSection] = useState<'identity' | 'avatar' | 'alignment' | 'skills'>('identity');
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/logout`, { withCredentials: true });
      if (socket) {
        socket.emit('logout');
        socket.disconnect();
      }
      setMercenary(null);
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [racesRes, templatesRes] = await Promise.all([
          axios.get('/api/mercenary/race-bonuses'),
          axios.get('/api/mercenary/skill-templates'),
        ]);
        setRaceBonuses(racesRes.data);
        setSkillTemplates(templatesRes.data);
      } catch {
        displayToast('Error fetching initial data.', 'error');
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-check: can this user actually create a mercenary right now?
  useEffect(() => {
    const checkCanCreate = async () => {
      const result = await fetchUserMercenaries();
      if (!result) return;
      if (!result.canCreate) {
        setCreationBlocked(true);
        if (result.dailyLimitReached) {
          const limitStr = result.dailyLimit ? ` (limit: ${result.dailyLimit} per day)` : '';
          let msg = `You have forged too many mercenaries today!(${limitStr}).`;
          if (result.nextAvailableAt) {
            const diffMs = new Date(result.nextAvailableAt).getTime() - Date.now();
            if (diffMs > 0) {
              const diffHrs = Math.floor(diffMs / 3600000);
              const diffMins = Math.ceil((diffMs % 3600000) / 60000);
              const timeStr = diffHrs > 0 ? `${diffHrs}h ${diffMins}m` : `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
              msg += ` Next slot opens in ${timeStr}.`;
            }
          }
          setCreationBlockedMessage(msg);
        } else {
          setCreationBlockedMessage('You cannot create a new mercenary yet. Check your existing mercenaries\' level requirements.');
        }
      }
    };
    checkCanCreate();
  }, [fetchUserMercenaries]);

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (name.trim()) {
        try {
          const response = await axios.get(`/api/mercenary/check-name?name=${encodeURIComponent(name.trim())}`);
          setIsNameAvailable(response.data.isAvailable);
        } catch {
          displayToast('Error checking name availability.', 'error');
        }
      } else {
        setIsNameAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [name]);

  const displayToast = (message: string, type: 'success' | 'error', duration = 4000) => {
    setToastMessage(message);
    setToastType(type);
    setToastDuration(duration);
  };

  const handlePrimaryChange = (skill: keyof PrimaryPoints, value: string) => {
    const parsedValue = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
    const newPoints = { ...primaryPoints, [skill]: parsedValue };
    const total = Object.values(newPoints).reduce((a, b) => a + b, 0);
    if (total <= 120) { setPrimaryPoints(newPoints); setPrimaryPointsLeft(120 - total); }
  };

  const handleSecondaryChange = (skill: keyof SecondaryPoints, value: string) => {
    const parsedValue = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
    const newPoints = { ...secondaryPoints, [skill]: parsedValue };
    const total = Object.values(newPoints).reduce((a, b) => a + b, 0);
    if (total <= 5) { setSecondaryPoints(newPoints); setSecondaryPointsLeft(5 - total); }
  };

  const applyTemplate = (t: SkillTemplate) => {
    if (activeTemplateId === t.id) {
      // Toggle off: clear all points
      setPrimaryPoints(initialPrimaryPoints);
      setSecondaryPoints(initialSecondaryPoints);
      setPrimaryPointsLeft(120);
      setSecondaryPointsLeft(5);
      setActiveTemplateId(null);
      return;
    }
    const pp: PrimaryPoints = {
      health: t.health, strength: t.strength, endurance: t.endurance, initiative: t.initiative,
      dodge: t.dodge, axe: t.axe, sword: t.sword, mace: t.mace, staff: t.staff,
      shield: t.shield, dagger: t.dagger, chain: t.chain,
    };
    const sp: SecondaryPoints = {
      learning: t.learning, luck: t.luck, discipline: t.discipline, leadership: t.leadership, provocation: t.provocation,
    };
    setPrimaryPoints(pp);
    setSecondaryPoints(sp);
    setPrimaryPointsLeft(120 - Object.values(pp).reduce((a, b) => a + b, 0));
    setSecondaryPointsLeft(5 - Object.values(sp).reduce((a, b) => a + b, 0));
    setActiveTemplateId(t.id);
  };

  const getSelectedRaceBonus = (skill: string): string | null => {
    const selectedRace = raceBonuses.find((r) => r.raceName === race);
    const mappedKey = weaponMapping[skill] || attributeMapping[skill] || secondarySkillMapping[skill];
    if (selectedRace && mappedKey && mappedKey in selectedRace) {
      const bonusValue = selectedRace[mappedKey as keyof RaceModifiers] ?? 0;
      const pct = (Number(bonusValue) - 1) * 100;
      if (pct === 0) return null;
      return pct > 0 ? `+${pct.toFixed(0)}%` : `${pct.toFixed(0)}%`;
    }
    return null;
  };

  const getAvatarGrid = () =>
    Array.from({ length: 9 }, (_, i) => i + 1).map((i) => (
      <div
        key={i}
        className={`${styles.avatarItem} ${avatar === i ? styles.avatarSelected : ''}`}
        onClick={() => setAvatar(i)}
      >
        <img
          src={`/img/avatars/${race.toLowerCase()}.${sex.toLowerCase()}.${i}.png`}
          alt={`Avatar ${i}`}
        />
        {avatar === i && <div className={styles.avatarCheckmark}>✓</div>}
      </div>
    ));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const issues: string[] = [];
    let nameIssue: string | null = null;
    if (!name.trim()) {
      nameIssue = 'Please enter a name for your mercenary.';
      issues.push('enter a name');
    } else if (name.trim().length < 3 || name.trim().length > 20) {
      nameIssue = 'Name must be between 3 and 20 characters.';
      issues.push('choose a name between 3 and 20 characters');
    } else if (isNameAvailable === false) {
      nameIssue = '⚔ That name is already taken, choose another.';
      issues.push('choose a different name, that one is already taken');
    } else if (isNameAvailable === null) {
      nameIssue = 'Still checking name availability, please wait a moment.';
      issues.push('wait a moment, name availability is still being checked');
    }
    if (nameIssue) {
      setNameError(nameIssue);
      setActiveSection('identity');
      setTimeout(() => nameInputRef.current?.focus(), 50);
    }
    if (avatar === null) issues.push('select an avatar');
    if (primaryPointsLeft > 0) issues.push(`allocate all primary skill points (${primaryPointsLeft} remaining)`);
    if (secondaryPointsLeft > 0) issues.push(`allocate all secondary skill points (${secondaryPointsLeft} remaining)`);
    if (primaryPoints.health < 20) issues.push('invest at least 20 points in Health (more is strongly recommended)');
    if (issues.length > 0) {
      const msg = issues.length === 1
        ? `Please ${issues[0]}.`
        : `Please fix the following before forging:\n• ${issues.join('\n• ')}`;
      displayToast(msg, 'error', issues.length > 1 ? 8000 : 4000);
      return;
    }
    try {
      const response = await axios.post(
        '/api/mercenary',
        { name, race, sex, avatar, alignment, ...primaryPoints, ...secondaryPoints },
        { withCredentials: true }
      );
      const newMercId = response.data.mercenaryId || response.data.newMercenary?.id;
      await switchMercenary({ id: newMercId });
      displayToast('Mercenary forged! A guide will walk you through what lies ahead.', 'success');
      navigate('/mercenary');
    } catch (error: any) {
      if (error.response?.data?.message) {
        let msg = error.response.data.message;
        const nextAt = error.response?.data?.nextAvailableAt;
        if (nextAt) {
          const nextDate = new Date(nextAt);
          const now = new Date();
          const diffMs = nextDate.getTime() - now.getTime();
          if (diffMs > 0) {
            const diffHrs = Math.floor(diffMs / 3600000);
            const diffMins = Math.ceil((diffMs % 3600000) / 60000);
            const timeStr = diffHrs > 0
              ? `${diffHrs}h ${diffMins}m`
              : `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
            msg += ` Next slot opens in ${timeStr}.`;
          } else {
            msg += ` You can try again now.`;
          }
        }
        displayToast(msg, 'error', 8000);
      } else {
        displayToast('Failed to create mercenary. Please try again.', 'error');
      }
    }
  };

  const filteredTemplates = skillTemplates.filter((t) => t.race === 'Any' || t.race === race);
  const primaryFillPct    = Math.round(((120 - primaryPointsLeft) / 120) * 100);
  const steps             = ['identity', 'avatar', 'alignment', 'skills'] as const;
  const stepLabels        = { identity: 'Identity', avatar: 'Avatar', alignment: 'Alignment', skills: 'Skills' };

  const renderSkillRow = (
    skill: string,
    value: number,
    onChange: (val: string) => void,
    onDec: () => void,
    onInc: () => void,
    disableInc: boolean
  ) => {
    const bonus = getSelectedRaceBonus(skill);
    return (
      <div key={skill} className={styles.skillRow}>
        <span className={styles.skillName} style={{ display: 'flex', alignItems: 'center' }}>
          {SKILL_DISPLAY[skill]}
          <span 
            className={styles.helpIcon}
            onClick={() => displayToast(SKILL_DESCRIPTIONS[skill], 'success')}
            title={SKILL_DESCRIPTIONS[skill] || ''} 
          >
            ?
          </span>
        </span>
        <div className={styles.skillStepper}>
          <HoldButton type="button" className={styles.stepperBtn} onClick={onDec} disabled={value === 0}>−</HoldButton>
          <input
            type="number"
            className={styles.skillInput}
            value={value === 0 ? '' : value}
            placeholder="0"
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            min="0"
          />
          <HoldButton type="button" className={styles.stepperBtn} onClick={onInc} disabled={disableInc}>+</HoldButton>
        </div>
        {bonus && (
          <span className={`${styles.racialBonus} ${bonus.startsWith('+') ? styles.bonusPositive : styles.bonusNegative}`}>
            {bonus}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.createMercenaryContainer}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={toastDuration} onClose={() => setToastMessage(null)} />
      )}

      {creationBlocked && (
        <div className={styles.creationBlockedBanner}>
          <span>⚠ {creationBlockedMessage}</span>
        </div>
      )}

      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Forge Your Mercenary</h1>
        <p className={styles.pageSubtitle}>Shape a legend from flesh, steel and will.</p>
        <button type="button" className={styles.logoutBtn} onClick={handleLogout}>Logout</button>
      </div>

      {/* Step navigation */}
      <div className={styles.stepNav}>
        {steps.map((step, idx) => (
          <React.Fragment key={step}>
            <button
              type="button"
              className={`${styles.stepTab} ${activeSection === step ? styles.stepTabActive : ''} ${step === 'identity' && nameError && activeSection !== 'identity' ? styles.stepTabError : ''}`}
              onClick={() => setActiveSection(step)}
            >
              <span className={styles.stepNumber}>{idx + 1}</span>
              {stepLabels[step]}
            </button>
            {idx < steps.length - 1 && <span className={styles.stepDivider}>›</span>}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit}>

        {/* ── IDENTITY ── */}
        <div className={`${styles.section} ${activeSection !== 'identity' ? styles.sectionHidden : ''}`}>
          <h2 className={styles.sectionTitle}>Identity</h2>
          <div className={styles.identityGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Mercenary Name</label>
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => { setName(e.target.value); setNameError(null); }}
                placeholder="Enter a name…"
                maxLength={20}
                className={`${styles.textInput}${nameError ? ` ${styles.textInputError}` : ''}`}
                required
              />
              {nameError && (
                <span className={styles.inputError}>{nameError}</span>
              )}
              {!nameError && name.length > 0 && isNameAvailable === false && (
                <span className={styles.inputError}>⚔ Name already taken</span>
              )}
              {!nameError && name.length > 0 && isNameAvailable === true && (
                <span className={styles.inputSuccess}>✓ Name is available</span>
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Sex</label>
              <select value={sex} onChange={(e) => setSex(e.target.value)} className={styles.selectInput} required>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          {/* Race selection cards */}
          <div className={styles.raceSection}>
            <span className={styles.raceSectionLabel}>Race</span>
            <div className={styles.raceGrid}>
              {raceBonuses.map((r) => {
                const cfg = RACE_CONFIG[r.raceName];
                return (
                  <button
                    key={r.raceName}
                    type="button"
                    className={`${styles.raceCard} ${race === r.raceName ? styles.raceCardSelected : ''}`}
                    onClick={() => { setRace(r.raceName); setActiveTemplateId(null); }}
                  >
                    <div className={styles.raceCardHeader}>
                      <span className={styles.raceCardName}>{r.raceName}</span>
                    </div>
                    {cfg && <p className={styles.raceCardLore}>{cfg.lore}</p>}
                    {cfg && (
                      <div className={styles.raceAbilities}>
                        {cfg.abilities.map((ab) => (
                          <span key={ab.label} className={styles.raceAbilityTag} title={ab.desc}>
                            {ab.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.stepActions}>
            <button type="button" className={styles.nextButton} onClick={() => setActiveSection('avatar')}>Continue →</button>
          </div>
        </div>

        {/* ── AVATAR ── */}
        <div className={`${styles.section} ${activeSection !== 'avatar' ? styles.sectionHidden : ''}`}>
          <h2 className={styles.sectionTitle}>Choose Your Avatar</h2>
          <div className={styles.avatarGrid}>{getAvatarGrid()}</div>
          <div className={styles.stepActions}>
            <button type="button" className={styles.backButton} onClick={() => setActiveSection('identity')}>← Back</button>
            <button type="button" className={styles.nextButton} onClick={() => setActiveSection('alignment')}>Continue →</button>
          </div>
        </div>

        {/* ── ALIGNMENT ── */}
        <div className={`${styles.section} ${activeSection !== 'alignment' ? styles.sectionHidden : ''}`}>
          <h2 className={styles.sectionTitle}>Choose Your Alignment</h2>
          <p className={styles.sectionNote}>Your alignment influences the likelihood of gaining or losing reputation.</p>
          <div className={styles.alignmentGrid}>
            {(Object.keys(ALIGNMENT_CONFIG) as Alignment[]).map((al) => {
              const cfg = ALIGNMENT_CONFIG[al];
              return (
                <button
                  key={al}
                  type="button"
                  className={`${styles.alignmentCard} ${styles[`align${al}`]} ${alignment === al ? styles.alignmentSelected : ''}`}
                  onClick={() => setAlignment(al)}
                >
                  <span className={styles.alignmentIcon}>{cfg.icon}</span>
                  <span className={styles.alignmentLabel}>{al}</span>
                  <span className={styles.alignmentDesc}>{cfg.description}</span>
                  <span className={styles.alignmentRepText}>{cfg.repText}</span>
                </button>
              );
            })}
          </div>
          <div className={styles.stepActions}>
            <button type="button" className={styles.backButton} onClick={() => setActiveSection('avatar')}>← Back</button>
            <button type="button" className={styles.nextButton} onClick={() => setActiveSection('skills')}>Continue →</button>
          </div>
        </div>

        {/* ── SKILLS ── */}
        <div className={`${styles.section} ${activeSection !== 'skills' ? styles.sectionHidden : ''}`}>
          <h2 className={styles.sectionTitle}>Distribute Skills</h2>

          {/* Points indicator */}
          <div className={styles.pointsPanel}>
            <div className={styles.pointsBudgetRow}>
              <span className={styles.pointsBudgetLabel}>Primary</span>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${primaryFillPct}%` }} />
              </div>
              <span className={`${styles.pointsCount} ${primaryPointsLeft === 0 ? styles.pointsDone : primaryPointsLeft < 10 ? styles.pointsLow : ''}`}>
                {primaryPointsLeft} left
              </span>
            </div>
            <div className={styles.pointsBudgetRow}>
              <span className={styles.pointsBudgetLabel}>Secondary</span>
              <div className={styles.progressBar}>
                <div className={styles.progressFillSecondary} style={{ width: `${((5 - secondaryPointsLeft) / 5) * 100}%` }} />
              </div>
              <span className={`${styles.pointsCount} ${secondaryPointsLeft === 0 ? styles.pointsDone : ''}`}>
                {secondaryPointsLeft} left
              </span>
            </div>
          </div>

          {/* Quick-build templates */}
          {filteredTemplates.length > 0 && (
            <div className={styles.templatesSection}>
              <h3 className={styles.templateTitle}>Quick-Build Templates</h3>
              <div className={styles.templatesList}>
                {filteredTemplates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`${styles.templateChip}${activeTemplateId === t.id ? ` ${styles.templateChipActive}` : ''}`}
                    onClick={() => applyTemplate(t)}
                    title={activeTemplateId === t.id ? 'Click again to clear points' : (t.description || t.name)}
                  >
                    {t.name}
                    {t.race !== 'Any' && <span className={styles.templateRace}>{t.race}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Skill table header */}
          <div className={styles.skillColumns}>
            <div className={styles.skillColumn}>
              <h3 className={styles.skillColumnTitle}>Attributes</h3>
              <div className={styles.skillColumnHeader}>
                <span>Skill</span><span>Allocate</span><span>Racial</span>
              </div>
              {PRIMARY_ATTRIBUTE_SKILLS.map((skill) =>
                renderSkillRow(
                  skill,
                  primaryPoints[skill],
                  (v) => handlePrimaryChange(skill, v),
                  () => handlePrimaryChange(skill, String(Math.max(0, primaryPoints[skill] - 1))),
                  () => handlePrimaryChange(skill, String(primaryPoints[skill] + 1)),
                  primaryPointsLeft === 0
                )
              )}
            </div>

            <div className={styles.skillColumn}>
              <h3 className={styles.skillColumnTitle}>Weapon Skills</h3>
              <div className={styles.skillColumnHeader}>
                <span>Skill</span><span>Allocate</span><span>Racial</span>
              </div>
              {PRIMARY_WEAPON_SKILLS.map((skill) =>
                renderSkillRow(
                  skill,
                  primaryPoints[skill],
                  (v) => handlePrimaryChange(skill, v),
                  () => handlePrimaryChange(skill, String(Math.max(0, primaryPoints[skill] - 1))),
                  () => handlePrimaryChange(skill, String(primaryPoints[skill] + 1)),
                  primaryPointsLeft === 0
                )
              )}
            </div>
          </div>

          {/* Secondary skills */}
          <div className={styles.secondarySection}>
            <h3 className={styles.skillColumnTitle}>
              Secondary Skills <span className={styles.secondaryNote}>(5 points total)</span>
            </h3>
            <div className={styles.skillColumnHeader}>
              <span>Skill</span><span>Allocate</span><span>Racial</span>
            </div>
            <div className={styles.secondaryGrid}>
              {SECONDARY_SKILLS.map((skill) =>
                renderSkillRow(
                  skill,
                  secondaryPoints[skill],
                  (v) => handleSecondaryChange(skill, v),
                  () => handleSecondaryChange(skill, String(Math.max(0, secondaryPoints[skill] - 1))),
                  () => handleSecondaryChange(skill, String(secondaryPoints[skill] + 1)),
                  secondaryPointsLeft === 0
                )
              )}
            </div>
          </div>

          <div className={styles.stepActions}>
            <button type="button" className={styles.backButton} onClick={() => setActiveSection('alignment')}>← Back</button>
            {(!name.trim() || name.trim().length < 3 || name.trim().length > 12 || isNameAvailable === false) && (
              <span className={styles.nameWarning}>
                ⚠ <button type="button" className={styles.nameLinkBtn} onClick={() => setActiveSection('identity')}>
                  {!name.trim()
                    ? 'Your mercenary needs a name'
                    : name.trim().length < 3 || name.trim().length > 20
                    ? 'Name must be 3–20 characters'
                    : 'That name is already taken'}
                </button>
              </span>
            )}
            <button type="submit" className={styles.submitButton} disabled={creationBlocked}>⚔ Forge Mercenary</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateMercenary;
